import { useCallback, useEffect, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import gql from 'graphql-tag';

const TERMINAL_STATUSES = ['COMPLETED', 'FAILED', 'CANCELLED', 'EXPIRED'] as const;
export type AsyncApiJobStatus = 'PENDING' | 'RUNNING' | (typeof TERMINAL_STATUSES)[number];
/** Τελική κατάσταση callback: terminal status job ή σφάλμα πριν/κατά την εκτέλεση */
export type AsyncApiJobOutcome = AsyncApiJobStatus | 'ERROR';

export type AsyncApiJobResult = {
  id: number;
  status: string;
  jobType: string | null;
  requestPayload: string | null;
  resultPayload: string | null;
  errorPayload: string | null;
  firstDate: string | null;
  lastDate: string | null;
  firstUser: string | null;
  lastUser: string | null;
};

const GET_ASYNC_API_JOB_BY_ID = gql`
  query getAsyncApiJobById($id: Int) {
    getAsyncApiJobById(id: $id) {
      id
      status
      jobType
      requestPayload
      resultPayload
      errorPayload
      firstDate
      lastDate
      firstUser
      lastUser
    }
  }
`;

type GetAsyncApiJobByIdResult = {
  getAsyncApiJobById: AsyncApiJobResult | null;
};

const POLL_INTERVAL_MS = 2000;
const DEFAULT_FETCH_OPTIONS: RequestInit = {
  method: 'POST',
  headers: { 'Content-Type': 'application/json', Accept: '*/*' },
};

export type UseAsyncApiJobOptions = {
  /** URL to POST to start the async job (response must contain job id, e.g. `{ id: number }`) */
  url: string;
  /** Κλήση σε terminal status job ή σε σφάλμα εκκίνησης/polling (status === 'ERROR'). */
  onJobFinished: (
    status: AsyncApiJobOutcome,
    job: AsyncApiJobResult | null,
    errorMessage?: string,
  ) => void;
  /** Optional fetch options (e.g. headers for auth). Merged with method POST and Content-Type. */
  fetchOptions?: RequestInit;
  /** Poll interval in ms (default 2000) */
  pollIntervalMs?: number;
};

export type UseAsyncApiJobReturn = {
  /** Start the async job: POST to url with body, then poll until terminal status and call onJobFinished */
  runJob: (body?: Record<string, unknown>) => Promise<void>;
  loading: boolean;
  error: string | null;
};

function isTerminalStatus(status: string): status is (typeof TERMINAL_STATUSES)[number] {
  return TERMINAL_STATUSES.includes(status as (typeof TERMINAL_STATUSES)[number]);
}

/** Μορφοποίηση μηνύματος σφάλματος από HTTP response (π.χ. JSON `{ error: "..." }`) */
function formatFetchError(raw: string): string {
  if (!raw) return 'Άγνωστο σφάλμα';
  try {
    const parsed = JSON.parse(raw) as { error?: string; message?: string };
    return parsed.error ?? parsed.message ?? raw;
  } catch {
    return raw;
  }
}

/**
 * ***************************************************************************************************************
 * Async Api Job
 * ***************************************************************************************************************
 * @param url - URL to POST to start the async job (response must contain job id, e.g. `{ id: number }`)
 * @param onJobFinished - Called when job reaches COMPLETED, FAILED, CANCELLED, or EXPIRED. Use to refresh data.
 * @param fetchOptions - Optional fetch options (e.g. headers for auth). Merged with method POST and Content-Type.
 * @param pollIntervalMs - Poll interval in ms (default 2000)
 * @returns runJob(body) - Pass body when calling; POST body is sent as JSON.
 */
export function useAsyncApiJob({
  url,
  onJobFinished,
  fetchOptions = {},
  pollIntervalMs = POLL_INTERVAL_MS,
}: UseAsyncApiJobOptions): UseAsyncApiJobReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef(false);

  const [getJobById] = useLazyQuery<GetAsyncApiJobByIdResult>(GET_ASYNC_API_JOB_BY_ID, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    return () => {
      abortRef.current = true;
    };
  }, []);

  const runJob = useCallback(
    async (body: Record<string, unknown> = {}) => {
      setError(null);
      setLoading(true);
      abortRef.current = false;

      try {
        const res = await fetch(url, {
          ...DEFAULT_FETCH_OPTIONS,
          ...fetchOptions,
          body: JSON.stringify(body),
        });

        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || `HTTP ${res.status}`);
        }

        const data = await res.json();
        const jobId = data.asyncJobId;

        if (jobId == null) {
          throw new Error('Δεν επιστράφηκε id job από το API');
        }

        const poll = (): Promise<void> =>
          new Promise((resolve) => {
            const check = async () => {
              if (abortRef.current) {
                setLoading(false);
                return resolve();
              }
              try {
                const result = await getJobById({ variables: { id: jobId } });
                const job = result.data?.getAsyncApiJobById ?? null;
                const status = (job?.status ?? '') as AsyncApiJobStatus;

                if (job && isTerminalStatus(status)) {
                  setLoading(false);
                  onJobFinished(status, job);
                  return resolve();
                }
              } catch (e) {
                setLoading(false);
                const message = e instanceof Error ? e.message : String(e);
                setError(message);
                onJobFinished('ERROR', null, message);
                return resolve();
              }
              setTimeout(check, pollIntervalMs);
            };
            check();
          });

        await poll();
      } catch (e) {
        setLoading(false);
        const message = formatFetchError(e instanceof Error ? e.message : String(e));
        setError(message);
        onJobFinished('ERROR', null, message);
      }
    },
    [url, onJobFinished, getJobById, pollIntervalMs, fetchOptions],
  );

  return { runJob, loading, error };
}
